/**
 * Minimal Supabase (GoTrue) auth client. When `VITE_SUPABASE_URL` +
 * `VITE_SUPABASE_ANON_KEY` are configured, the hosted login authenticates
 * against Supabase and exchanges the returned access token for a first-party
 * Hive session via POST /api/auth/login/supabase. Unset ⇒ the login page keeps
 * the classic local email/password form, so dev setups need no Supabase.
 *
 * Plain fetch on the two GoTrue endpoints we need — not @supabase/supabase-js —
 * because Hive only ever holds the token long enough to trade it for its own
 * session cookie; no client-side session persistence is wanted.
 */

const URL_ = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/+$/, "");
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

export function supabaseEnabled(): boolean {
  return URL_.length > 0 && ANON_KEY.length > 0;
}

export class SupabaseAuthError extends Error {}

interface TokenResponse {
  access_token?: string;
  user?: { email?: string };
  /** signup with email confirmation enabled returns a user but no session */
  confirmation_sent_at?: string;
}

async function gotrue(path: string, body: Record<string, unknown>): Promise<TokenResponse> {
  const res = await fetch(`${URL_}/auth/v1/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: ANON_KEY },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const msg =
      (data.error_description as string) ||
      (data.msg as string) ||
      (data.message as string) ||
      "Authentication failed";
    throw new SupabaseAuthError(msg);
  }
  return data as TokenResponse;
}

/** Password sign-in → access token. */
export async function supabaseSignIn(email: string, password: string): Promise<string> {
  const data = await gotrue("token?grant_type=password", { email, password });
  if (!data.access_token) throw new SupabaseAuthError("No access token returned");
  return data.access_token;
}

/**
 * Sign-up → access token when the project auto-confirms emails (ours does),
 * or null when Supabase sent a confirmation email first.
 */
export async function supabaseSignUp(
  email: string,
  password: string,
  name?: string,
): Promise<string | null> {
  const data = await gotrue("signup", {
    email,
    password,
    data: name ? { full_name: name } : {},
  });
  return data.access_token ?? null;
}

/**
 * Google OAuth happens as a full-page redirect through Supabase. Supabase
 * sends the browser back to `redirectTo` with tokens in the URL fragment.
 * NOTE: `redirectTo` must be allowlisted in the Supabase dashboard
 * (Authentication → URL Configuration → Redirect URLs).
 */
export function supabaseGoogleUrl(redirectTo: string): string {
  const params = new URLSearchParams({ provider: "google", redirect_to: redirectTo });
  return `${URL_}/auth/v1/authorize?${params}`;
}

/** Tokens (or error) Supabase put in the URL fragment after an OAuth redirect. */
export function parseOAuthFragment(hash: string): { accessToken?: string; error?: string } {
  if (!hash.startsWith("#")) return {};
  const params = new URLSearchParams(hash.slice(1));
  const error = params.get("error_description") || params.get("error");
  if (error) return { error };
  const accessToken = params.get("access_token");
  return accessToken ? { accessToken } : {};
}
